import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  OpenInNew,
  PlayArrow,
  Science,
  VideoCall,
  CloudDownloadOutlined,
  Mic,
} from '@mui/icons-material';
import { useAuth } from '../context/EnterpriseAuthContext';
import { useUsage } from '../context/UsageContext';
import { useAIRequests } from '../context/AIRequestContext';
import { hasReachedLimit, getUserTier } from '../services/usageService';
import { isAutopilotEnabled } from '../services/autopilotModel';
import { supabase } from '../lib/supabase';
import { extractionConfigToLegacyExtractionTypeArray } from '../utils/extractionTaxonomy';
import { sessionPath } from '../utils/navigation';
import ProjectContextPanel from './ProjectContextPanel';
import SessionEditorDialog from './SessionEditorDialog';
import { SectionCard, EmptyState, ConfirmDialog } from './pagekit';
import CreateSessionDialog from './sessions/CreateSessionDialog';
import SessionRow from './sessions/SessionRow';
import { RegenerateRequirementsDialog, ExtractRequirementsDialog } from './sessions/ExtractionDialogs';
import { FathomImportDialog, DomainConflictDialog, DomainReviewDialog } from './sessions/FathomDialogs';
import NotetakerDialog, { useNotetakerAvailability } from './sessions/NotetakerDialog';
import useFathomImport from './sessions/useFathomImport';
import { normaliseTranscript } from './sessions/transcriptUtils';

/**
 * Sessions step of a project: meetings and files that requirements are
 * extracted from, plus the project's context files.
 *
 * @param {object} props
 * @param {object} props.project - Project.
 * @param {object|null} props.projectCustomer - Customer of the project.
 * @param {Function} props.onCreateSession - async (formData) => void (create + extract, or regenerate).
 * @param {Function} props.onUpdateSession - async (sessionId, updates) => {success}.
 * @param {Function} props.onDeleteSession - (sessionId) => void.
 * @param {Function} [props.onExtractSession] - async ({sessionId, extractionConfig, extractionTypes, targetedExtraction}).
 * @param {Function} [props.onUpdateCustomer] - async (customerId, updates).
 * @param {Function} [props.findCustomerByDomain] - (domain) => customer|null.
 * @param {boolean} props.extracting - Extraction dispatch in flight.
 * @param {string} [props.extractionError] - Last extraction error.
 * @param {Function} [props.onImportFathomSessions] - async (meetingData) => result.
 * @param {Function} [props.onGoToRequirements] - Switch to the requirements step.
 * @param {'create'|'extract'|null} [props.pendingAction] - One-shot request from the parent (the
 *   pipeline next-step button): 'create' opens the new-session dialog, 'extract' opens the extract
 *   dialog for the first session with nothing extracted yet.
 * @param {Function} [props.onPendingActionHandled] - Called once the pending action ran, so the
 *   parent can clear it (it must not run again when the tab remounts).
 * @returns {JSX.Element}
 */
const SessionsTab = memo(({
  project,
  projectCustomer,
  onCreateSession,
  onUpdateSession,
  onDeleteSession,
  onExtractSession,
  onUpdateCustomer,
  findCustomerByDomain,
  extracting,
  extractionError,
  onImportFathomSessions,
  onGoToRequirements,
  pendingAction = null,
  onPendingActionHandled,
}) => {
  const navigate = useNavigate();
  const { state: authState, actions: authActions } = useAuth();
  const { refreshUsage } = useUsage();
  const { activeRequests } = useAIRequests();
  const userTier = getUserTier(authState.currentMembership, authActions.isAccountAdmin());
  const notetakerAvailable = useNotetakerAvailability({ userId: authState.user?.id, userTier });
  // Meeting Autopilot: offer "Run autopilot" on new uploads when the account has the feature.
  const autopilotAvailable = isAutopilotEnabled(authState.activeAccount);

  const fathom = useFathomImport({
    authState,
    onImportFathomSessions,
    projectCustomer,
    findCustomerByDomain,
    onUpdateCustomer,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [limitReachedOpen, setLimitReachedOpen] = useState(false);
  const [notetakerOpen, setNotetakerOpen] = useState(false);
  const [menu, setMenu] = useState({ anchor: null, session: null });
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [sessionToRegenerate, setSessionToRegenerate] = useState(null);
  const [sessionToExtract, setSessionToExtract] = useState(null);
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState('');
  const [snackbar, setSnackbar] = useState('');

  // Transcripts are not part of the project list payload; load them on demand.
  const [expandedSessions, setExpandedSessions] = useState({});
  const [loadedTranscripts, setLoadedTranscripts] = useState({});
  const [loadingTranscripts, setLoadingTranscripts] = useState({});

  const sessions = useMemo(() => project.sessions || [], [project.sessions]);

  const requirementCounts = useMemo(() => {
    const counts = {};
    (project.requirements || []).forEach((r) => {
      if (r.sessionId) counts[r.sessionId] = (counts[r.sessionId] || 0) + 1;
    });
    return counts;
  }, [project.requirements]);

  /** Sessions with a running extraction job. */
  const extractingSessionIds = useMemo(() => {
    const ids = new Set();
    (activeRequests || []).forEach((req) => {
      if (req?.type === 'requirement_extraction' && req.request_data?.sessionId) {
        ids.add(req.request_data.sessionId);
      }
    });
    return ids;
  }, [activeRequests]);

  /**
   * Load a session's transcript (cached).
   * @param {string} sessionId - Session id.
   * @returns {Promise<object|null>} Normalised transcript.
   */
  const fetchTranscript = useCallback(async (sessionId) => {
    if (loadedTranscripts[sessionId]) return loadedTranscripts[sessionId];
    const localSession = sessions.find((s) => s.id === sessionId);
    if (localSession?.transcript) return normaliseTranscript(localSession.transcript);

    setLoadingTranscripts((prev) => ({ ...prev, [sessionId]: true }));
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('transcript')
        .eq('id', sessionId)
        .single();
      if (error) throw error;
      const parsed = normaliseTranscript(data?.transcript);
      setLoadedTranscripts((prev) => ({ ...prev, [sessionId]: parsed }));
      return parsed;
    } catch (err) {
      console.error('Failed to load transcript', err);
      return null;
    } finally {
      setLoadingTranscripts((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, [loadedTranscripts, sessions]);

  const handleToggleExpand = useCallback(async (sessionId) => {
    if (!expandedSessions[sessionId]) {
      await fetchTranscript(sessionId);
    }
    setExpandedSessions((prev) => ({ ...prev, [sessionId]: !prev[sessionId] }));
  }, [expandedSessions, fetchTranscript]);

  const handleOpenCreate = useCallback(async () => {
    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'sessions')) {
      setLimitReachedOpen(true);
      return;
    }
    setCreateOpen(true);
  }, [refreshUsage]);

  // Run a one-shot action the parent asked for (pipeline next-step button).
  useEffect(() => {
    if (!pendingAction) return;
    onPendingActionHandled?.();
    if (pendingAction === 'create') {
      handleOpenCreate();
      return;
    }
    if (pendingAction === 'extract') {
      const candidate = sessions.find(
        (s) => !requirementCounts[s.id] && !extractingSessionIds.has(s.id)
      );
      if (candidate && onExtractSession) {
        setSessionToExtract(candidate);
      } else if (sessions.some((s) => extractingSessionIds.has(s.id))) {
        setSnackbar('Extraction is already running. New requirements appear when the job finishes.');
      } else if (!sessions.length) {
        handleOpenCreate();
      } else {
        setSnackbar('Every session already has requirements. Use Regenerate requirements from a session menu to run it again.');
      }
    }
  }, [pendingAction, onPendingActionHandled, handleOpenCreate, sessions, requirementCounts, extractingSessionIds, onExtractSession]);

  const closeMenu = useCallback(() => setMenu({ anchor: null, session: null }), []);

  /**
   * Close the menu, then run an action for its session. Focus is released
   * first so the closing menu never hides a focused element.
   * @param {(session: object) => void} action - Action.
   */
  const fromMenu = (action) => () => {
    const { session } = menu;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    closeMenu();
    if (session) action(session);
  };

  const handleRegenerate = useCallback(async ({ regenerateMode, extractionConfig, targetedExtraction }) => {
    const session = sessionToRegenerate;
    if (!session) return;

    const usageData = await refreshUsage();
    if (usageData && hasReachedLimit(usageData, 'sessions')) {
      setSessionToRegenerate(null);
      setLimitReachedOpen(true);
      return;
    }

    setSessionToRegenerate(null);
    const result = await onCreateSession({
      name: session.name || 'Untitled session',
      description: session.description,
      file: null,
      pastedContent: '',
      contentType: 'file',
      sessionId: session.id,
      regenerateMode,
      extractionConfig,
      extractionType: extractionConfigToLegacyExtractionTypeArray(extractionConfig),
      targetedExtraction,
    });
    refreshUsage();
    if (result?.success !== false) {
      setSnackbar('Extraction started. New requirements appear when the job finishes.');
    }
  }, [sessionToRegenerate, refreshUsage, onCreateSession]);

  const handleExtract = useCallback(async ({ extractionConfig, targetedExtraction }) => {
    const session = sessionToExtract;
    if (!session || !onExtractSession) return;
    setSessionToExtract(null);
    const result = await onExtractSession({
      sessionId: session.id,
      extractionConfig,
      extractionTypes: extractionConfigToLegacyExtractionTypeArray(extractionConfig),
      targetedExtraction,
    });
    if (result?.success !== false) {
      setSnackbar('Extraction started. New requirements appear when the job finishes.');
    }
  }, [sessionToExtract, onExtractSession]);

  const handleOpenEdit = useCallback(async (session) => {
    const transcript = loadedTranscripts[session.id] || session.transcript || await fetchTranscript(session.id);
    setSessionToEdit({ ...session, transcript: transcript || null });
  }, [loadedTranscripts, fetchTranscript]);

  const handleSaveEdit = useCallback(async (updates) => {
    if (!sessionToEdit || !onUpdateSession) return { success: false, error: 'Editing is not available.' };
    setEditSaving(true);
    try {
      const result = await onUpdateSession(sessionToEdit.id, updates);
      if (!result?.success) {
        return { success: false, error: result?.error || 'Failed to update session' };
      }
      if (updates.transcript !== undefined) {
        setLoadedTranscripts((prev) => ({ ...prev, [sessionToEdit.id]: normaliseTranscript(updates.transcript) }));
      }
      setSessionToEdit(null);
      return result;
    } finally {
      setEditSaving(false);
    }
  }, [sessionToEdit, onUpdateSession]);

  const showError = extractionError && extractionError !== errorDismissed;

  const headerActions = (
    <>
      {notetakerAvailable && (
        <Button variant="outlined" size="small" startIcon={<VideoCall />} onClick={() => setNotetakerOpen(true)}>
          Record live meeting
        </Button>
      )}
      {fathom.hasIntegration && (
        <Button variant="outlined" size="small" startIcon={<CloudDownloadOutlined />} onClick={fathom.openImport}>
          Import from Fathom
        </Button>
      )}
      <Button variant="contained" size="small" startIcon={<Add />} onClick={handleOpenCreate}>
        New session
      </Button>
    </>
  );

  return (
    <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
      {showError && (
        <Alert severity="error" onClose={() => setErrorDismissed(extractionError)}>
          {extractionError}
        </Alert>
      )}

      <SectionCard
        flush
        title="Sessions"
        description="Meetings, transcripts and documents. Requirements are extracted from each one."
        actions={headerActions}
      >
        {!sessions.length ? (
          <EmptyState
            icon={<Mic />}
            title="No sessions yet"
            description="Upload a transcript or notes, paste text, or import a recorded meeting. Requirements are extracted automatically."
            actions={
              <>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
                  New session
                </Button>
                {fathom.hasIntegration && (
                  <Button variant="outlined" startIcon={<CloudDownloadOutlined />} onClick={fathom.openImport}>
                    Import from Fathom
                  </Button>
                )}
              </>
            }
          />
        ) : (
          <Box component="ul" sx={{ m: 0, p: 0 }} aria-label="Sessions">
            {sessions.map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                requirementCount={requirementCounts[session.id] || 0}
                isExtracting={extractingSessionIds.has(session.id)}
                expanded={Boolean(expandedSessions[session.id])}
                loadingTranscript={Boolean(loadingTranscripts[session.id])}
                transcript={loadedTranscripts[session.id] || normaliseTranscript(session.transcript)}
                onOpen={() => navigate(sessionPath(session.id))}
                onToggleExpand={() => handleToggleExpand(session.id)}
                onExtract={onExtractSession ? () => setSessionToExtract(session) : undefined}
                onMenu={(e) => setMenu({ anchor: e.currentTarget, session })}
                onViewRequirements={onGoToRequirements}
              />
            ))}
          </Box>
        )}
      </SectionCard>

      <ProjectContextPanel project={project} accountId={authState.activeAccount?.id} />

      {/* Session actions */}
      <Menu
        anchorEl={menu.anchor}
        open={Boolean(menu.anchor)}
        onClose={closeMenu}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={fromMenu((s) => navigate(sessionPath(s.id)))}>
          <ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon>
          <ListItemText primary="Open session" />
        </MenuItem>
        <MenuItem onClick={fromMenu(handleOpenEdit)}>
          <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit session" />
        </MenuItem>
        {menu.session && !requirementCounts[menu.session.id] && onExtractSession && (
          <MenuItem onClick={fromMenu(setSessionToExtract)}>
            <ListItemIcon><Science fontSize="small" /></ListItemIcon>
            <ListItemText primary="Extract requirements" />
          </MenuItem>
        )}
        <MenuItem onClick={fromMenu(setSessionToRegenerate)}>
          <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon>
          <ListItemText primary="Regenerate requirements" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={fromMenu(setSessionToDelete)} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'inherit' }}><Delete fontSize="small" /></ListItemIcon>
          <ListItemText primary="Delete session" />
        </MenuItem>
      </Menu>

      <CreateSessionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreateSession={onCreateSession}
        onLimitReached={() => setLimitReachedOpen(true)}
        extracting={extracting}
        extractionError={createOpen ? extractionError : ''}
        autopilotAvailable={autopilotAvailable}
      />

      <Dialog open={limitReachedOpen} onClose={() => setLimitReachedOpen(false)}>
        <DialogTitle>Session limit reached</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have used this month's sessions. Upgrade your plan to add more.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitReachedOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              setLimitReachedOpen(false);
              window.dispatchEvent(new Event('quillai:open-settings'));
            }}
          >
            See plans
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(sessionToDelete)}
        title="Delete session?"
        message={`"${sessionToDelete?.name || 'This session'}" and every requirement extracted from it will be deleted. This cannot be undone.`}
        confirmLabel="Delete session"
        onConfirm={() => {
          if (sessionToDelete) onDeleteSession(sessionToDelete.id);
          setSessionToDelete(null);
        }}
        onClose={() => setSessionToDelete(null)}
      />

      <RegenerateRequirementsDialog
        open={Boolean(sessionToRegenerate)}
        session={sessionToRegenerate}
        extracting={extracting}
        extractionError={extractionError}
        onCancel={() => setSessionToRegenerate(null)}
        onConfirm={handleRegenerate}
      />

      <ExtractRequirementsDialog
        open={Boolean(sessionToExtract)}
        session={sessionToExtract}
        extracting={extracting}
        extractionError={extractionError}
        onClose={() => setSessionToExtract(null)}
        onConfirm={handleExtract}
      />

      <SessionEditorDialog
        open={Boolean(sessionToEdit)}
        onClose={() => setSessionToEdit(null)}
        session={sessionToEdit}
        project={project}
        onSave={handleSaveEdit}
        saving={editSaving}
      />

      <FathomImportDialog fathom={fathom} />
      <DomainConflictDialog fathom={fathom} />
      <DomainReviewDialog fathom={fathom} />

      <NotetakerDialog
        open={notetakerOpen}
        onClose={() => setNotetakerOpen(false)}
        userId={authState.user?.id}
        accountId={authState.activeAccount?.id}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar('')}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
});

SessionsTab.displayName = 'SessionsTab';

export default SessionsTab;
